import { useState, useEffect, lazy, Suspense } from "react";
import Navigation from "@/components/Navigation";
import HeroSection from "@/components/HeroSection";
import Footer from "@/components/Footer";
import WaveDivider from "@/components/WaveDivider";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import SEO from "@/components/SEO";
const InteractiveMap = lazy(() => import("@/components/InteractiveMap"));
import { MapPin, X } from "lucide-react";
import axios from "axios";
import DiveCenterCard from "@/components/DiveCenterCard";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const Map = () => {
  const [focusedCenter, setFocusedCenter] = useState(null);
  const [diveCenterInfoList, setDiveCenterInfoList] = useState([]);
  const [selectedLocationName, setSelectedLocationName] = useState('');
  const [timelineMonths, setTimelineMonths] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [allDiveCenters, setAllDiveCenters] = useState([]);

  // Color palette for locations
  const locationColors = {
    0: { bg: 'from-cyan-400 to-blue-500', ring: 'ring-cyan-200', dot: 'bg-cyan-400', hex: '#22d3ee' },
    1: { bg: 'from-purple-400 to-pink-500', ring: 'ring-purple-200', dot: 'bg-purple-400', hex: '#c084fc' },
    2: { bg: 'from-green-400 to-teal-500', ring: 'ring-green-200', dot: 'bg-green-400', hex: '#34d399' },
    3: { bg: 'from-orange-400 to-red-500', ring: 'ring-orange-200', dot: 'bg-orange-400', hex: '#fb923c' },
    4: { bg: 'from-yellow-400 to-amber-500', ring: 'ring-yellow-200', dot: 'bg-yellow-400', hex: '#fbbf24' },
    5: { bg: 'from-indigo-400 to-violet-500', ring: 'ring-indigo-200', dot: 'bg-indigo-400', hex: '#818cf8' },
  };

  const getLocationColor = (locationId) => {
    if (!locationId) return null;
    const locIndex = allLocations.findIndex(l => l.id === locationId);
    if (locIndex >= 0) return locationColors[locIndex % 6] || locationColors[0];
    const centerIndex = allDiveCenters.findIndex(c => c.id === locationId);
    return locationColors[centerIndex % 6] || locationColors[0];
  };

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Fetch all dive centers and locations for color mapping
    const fetchDiveCenters = async () => {
      try {
        const [centersRes, locationsRes] = await Promise.all([
          axios.get(`${API}/map/dive-centers`),
          axios.get(`${API}/locations`)
        ]);
        setAllDiveCenters(centersRes.data || []);
        setAllLocations(locationsRes.data || []);
      } catch (error) {
        console.error('Error fetching dive centers:', error);
      }
    };

    // Fetch timeline (full 12 months for map page)
    const fetchTimeline = async () => {
      try {
        const response = await axios.get(`${API}/timeline`);
        setTimelineMonths(response.data.months || []);
      } catch (error) {
        console.error('Error fetching timeline:', error);
      }
    };

    fetchDiveCenters();
    fetchTimeline();
  }, []);

  const handleViewOnMap = (centerId) => {
    const mapSection = document.querySelector('#map-section');
    if (mapSection) {
      mapSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    if (centerId) {
      setTimeout(() => setFocusedCenter(centerId), 500);
    }
  };

  const handleMonthClick = (month) => {
    if (!month.location_id && !month.dive_center_id) return;

    // Collect all location_ids for this month+year (handles split months)
    const locationIds = new Set();
    const dcIds = new Set();
    timelineMonths.forEach(m => {
      if (m.month === month.month && m.year === month.year) {
        if (m.location_id) locationIds.add(m.location_id);
        if (m.dive_center_id) dcIds.add(m.dive_center_id);
      }
    });

    let centers = allDiveCenters.filter(c => locationIds.has(c.location_id));
    if (centers.length === 0) {
      centers = allDiveCenters.filter(c => dcIds.has(c.id));
    } else {
      dcIds.forEach(dcId => {
        if (!centers.some(c => c.id === dcId)) {
          const match = allDiveCenters.find(c => c.id === dcId);
          if (match) centers.push(match);
        }
      });
    }

    if (centers.length > 0) {
      setDiveCenterInfoList(centers);
      setSelectedLocationName(`${centers[0].location}, ${centers[0].country}`);

      setTimeout(() => {
        const section = document.querySelector('[data-testid="map-dive-center-section"]');
        if (section) section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Dive Map - George Dives Locations & Dive Sites"
        description="Explore dive centers and dive sites around the world where George teaches. Interactive map with real-time dive center locations."
        keywords={['dive map', 'dive centers', 'dive sites', 'diving locations']}
      />
      
      <Navigation />

      {/* Header Section */}
      <HeroSection 
        title="Explore Dive Locations,"
        subtitle="Around the World"
        description="Discover dive centers and amazing dive sites where I teach and guide divers."
        backgroundImage="https://images.unsplash.com/photo-1559827260-dc66d52bef19?crop=entropy&cs=srgb&fm=jpg&q=85"
      />

      <WaveDivider variant="smooth" />

      {/* Interactive Map Section */}
      <section id="map-section" className="py-12">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="mb-8 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Interactive Dive Map</h2>
          </div>
          
          <div className="relative max-w-5xl mx-auto">
            <Suspense fallback={
              <div className="h-[500px] rounded-xl bg-slate-100 flex items-center justify-center">
                <div className="text-center text-slate-400">
                  <div className="w-10 h-10 border-4 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-sm font-medium">Loading map...</p>
                </div>
              </div>
            }>
              <InteractiveMap focusCenter={focusedCenter} />
            </Suspense>
          </div>
        </div>
      </section>

      {/* Timeline Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-100 text-cyan-700 mb-4">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-semibold">Travel Schedule</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
              Where I'll Be Teaching
            </h2>
            <p className="text-gray-500 text-sm">Click a month to see the dive centers</p>
          </div>

          {/* Timeline grid */}
          <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-12 gap-3 sm:gap-4" data-testid="map-timeline-grid">
            {(() => {
              // Group entries by month+year to handle split months
              const grouped = [];
              const visited = new Set();
              timelineMonths.forEach((month, idx) => {
                if (visited.has(idx)) return;
                const key = `${month.month}-${month.year}`;
                const partners = [];
                timelineMonths.forEach((m, j) => {
                  if (j !== idx && !visited.has(j) && `${m.month}-${m.year}` === key) {
                    partners.push(m);
                    visited.add(j);
                  }
                });
                visited.add(idx);
                grouped.push({ primary: month, partners });
              });

              return grouped.map((group, gIdx) => {
                const month = group.primary;
                const isCurrent = month.is_current || group.partners.some(p => p.is_current);
                const hasLocation = (month.location_id || month.dive_center_id) && month.location_name;
                const locationColor = getLocationColor(month.location_id || month.dive_center_id);
                const hasPartner = group.partners.length > 0 && group.partners[0].location_name;
                const partnerColor = hasPartner ? getLocationColor(group.partners[0].location_id || group.partners[0].dive_center_id) : null;

                return (
                  <button
                    key={gIdx}
                    onClick={() => handleMonthClick(month)}
                    disabled={!hasLocation}
                    className={`flex flex-col items-center gap-1.5 py-3 px-1 rounded-xl transition-all duration-200 ${
                      hasLocation
                        ? 'cursor-pointer hover:scale-105 hover:shadow-md'
                        : 'cursor-default opacity-50'
                    } ${
                      isCurrent
                        ? 'bg-white shadow-lg ring-2 ring-cyan-400 scale-105'
                        : hasLocation
                        ? 'bg-white/70 shadow-sm hover:bg-white'
                        : 'bg-white/30'
                    }`}
                    data-testid={`map-timeline-month-${month.month}-${month.year}`}
                  >
                    {/* Dot — split for shared months */}
                    {hasPartner && partnerColor ? (
                      <div
                        className={`w-3.5 h-3.5 rounded-full ${isCurrent ? 'ring-2 ring-cyan-200 ring-offset-1' : ''}`}
                        style={{
                          background: `linear-gradient(135deg, ${locationColor?.hex || '#22d3ee'} 50%, ${partnerColor?.hex || '#c084fc'} 50%)`,
                        }}
                      />
                    ) : (
                      <div className={`w-3.5 h-3.5 rounded-full ${
                        isCurrent
                          ? 'bg-cyan-400 ring-2 ring-cyan-200 ring-offset-1'
                          : hasLocation && locationColor
                          ? locationColor.dot
                          : 'bg-gray-300'
                      }`} />
                    )}

                    {/* Month */}
                    <span className={`text-sm font-bold tracking-wide ${
                      isCurrent ? 'text-slate-900' : hasLocation ? 'text-slate-700' : 'text-slate-400'
                    }`}>
                      {month.month}
                    </span>

                    {/* Year */}
                    <span className={`text-[10px] ${isCurrent ? 'text-slate-500' : 'text-slate-400'}`}>
                      {month.year}
                    </span>

                    {/* Current badge */}
                    {isCurrent && (
                      <span className="text-[9px] font-bold text-cyan-600 bg-cyan-100 px-2 py-0.5 rounded-full">NOW</span>
                    )}

                    {/* Location names */}
                    {hasLocation && (
                      <span className={`text-[9px] truncate max-w-full px-1 ${isCurrent ? 'text-cyan-600 font-medium' : 'text-gray-500'}`}>
                        {month.location_name.split(',')[0]}
                      </span>
                    )}
                    {hasPartner && (
                      <span className="text-[9px] text-gray-400 truncate max-w-full px-1">
                        {group.partners[0].location_name.split(',')[0]}
                      </span>
                    )}
                  </button>
                );
              });
            })()}
          </div>

          {/* Dive Center Cards — shown when a month is clicked */}
          {diveCenterInfoList.length > 0 && (() => {
            // Group centers by location for split-month display
            const grouped = [];
            const seen = new Set();
            diveCenterInfoList.forEach(center => {
              const key = center.location_id || center.id;
              if (!seen.has(key)) {
                seen.add(key);
                grouped.push({
                  locationId: center.location_id,
                  locationLabel: `${center.location}, ${center.country}`,
                  centers: diveCenterInfoList.filter(c => (c.location_id || c.id) === key),
                });
              }
            });
            const multiLocation = grouped.length > 1;

            return (
              <div className="max-w-5xl mx-auto mt-16" data-testid="map-dive-center-section">
                <div className="text-center mb-8 relative">
                  <button
                    onClick={() => setDiveCenterInfoList([])}
                    className="absolute right-0 top-0 p-2 rounded-full hover:bg-gray-100 transition-colors"
                    data-testid="map-close-dive-center-card"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                  <p className="text-sm text-cyan-700 font-semibold mb-2">
                    {diveCenterInfoList.length > 1 ? 'PARTNER DIVE CENTERS' : 'PARTNER DIVE CENTER'}
                  </p>
                  {!multiLocation && (
                    <h3 className="text-3xl font-bold text-slate-900 mb-2">{grouped[0].locationLabel}</h3>
                  )}
                </div>
                <div className="space-y-10">
                  {grouped.map((group, gIdx) => (
                    <div key={group.locationId || gIdx}>
                      {multiLocation && (
                        <div className="flex items-center gap-3 mb-4" data-testid={`map-location-group-${gIdx}`}>
                          <div className={`w-3 h-3 rounded-full shrink-0 ${
                            gIdx === 0 ? 'bg-cyan-400' : gIdx === 1 ? 'bg-purple-400' : 'bg-emerald-400'
                          }`} />
                          <h3 className="text-xl font-bold text-slate-800">{group.locationLabel}</h3>
                          <div className="flex-1 h-px bg-slate-200" />
                        </div>
                      )}
                      <div className={group.centers.length > 1 ? 'space-y-6' : ''}>
                        {group.centers.map(center => (
                          <DiveCenterCard
                            key={center.id}
                            diveCenterInfo={center}
                            onViewMap={() => handleViewOnMap(center.id)}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      <Footer />
      
      <FloatingUserMenu />
    </div>
  );
};

export default Map;